import React, { useRef, useState, useEffect, useCallback } from "react";

const isVideoUrl = (url) =>
    /\.(mp4|mov|webm|avi|mkv)(\?|$)/i.test(url);

// iOS Safari/WebView는 재생 전 paused <video>의 첫 프레임을 그리지 않는다.
// 미디어 프래그먼트(#t=0.1)로 첫 프레임을 포스터처럼 노출해, 자동재생이 안 된
// 슬라이드(상세/비활성 슬라이드)가 빈 칸으로 보이지 않게 한다.
const posterFrameSrc = (url) => `${url}#t=0.1`;

const MUTE_KEY = "community_video_muted";
const MUTE_EVENT = "community-mute-toggle";

const getGlobalMuted = () => {
    try {
        return sessionStorage.getItem(MUTE_KEY) !== "false";
    } catch {
        return true;
    }
};

const setGlobalMuted = (muted) => {
    try {
        sessionStorage.setItem(MUTE_KEY, String(muted));
    } catch {}
    window.dispatchEvent(new CustomEvent(MUTE_EVENT, { detail: { muted } }));
};

const MuteIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M11 5L6 9H2v6h4l5 4V5z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <line x1="23" y1="9" x2="17" y2="15" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
        <line x1="17" y1="9" x2="23" y2="15" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
    </svg>
);

const UnmuteIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M11 5L6 9H2v6h4l5 4V5z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
    </svg>
);

const MediaItem = ({ url, index, onClick, onRatio }) => {
    const videoRef = useRef(null);
    const [isMuted, setIsMuted] = useState(getGlobalMuted);

    // 다른 MediaItem에서 토글 시 동기화
    useEffect(() => {
        const handler = (e) => setIsMuted(e.detail.muted);
        window.addEventListener(MUTE_EVENT, handler);
        return () => window.removeEventListener(MUTE_EVENT, handler);
    }, []);

    // IntersectionObserver: 뷰포트 진입 시 play, 벗어나면 pause
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    video.play().catch(() => {});
                } else {
                    video.pause();
                }
            },
            { threshold: 0.5 }
        );

        observer.observe(video);
        return () => observer.disconnect();
    }, []);

    const handleMuteToggle = useCallback((e) => {
        e.stopPropagation();
        const next = !isMuted;
        setGlobalMuted(next);
    }, [isMuted]);

    const isVideo = isVideoUrl(url);
    if (isVideo) {
        return (
            <div className="media-item-wrapper" onClick={(e) => onClick(index, e)}>
                <video
                    ref={videoRef}
                    src={posterFrameSrc(url)}
                    className="media-item"
                    muted={isMuted}
                    playsInline
                    loop
                    preload="metadata"
                    onLoadedMetadata={(e) =>
                        onRatio && onRatio(e.target.videoWidth, e.target.videoHeight)
                    }
                />
                <button
                    className="media-mute-toggle"
                    onClick={handleMuteToggle}
                    aria-label={isMuted ? "음소거 해제" : "음소거"}
                    type="button"
                >
                    {isMuted ? <MuteIcon /> : <UnmuteIcon />}
                </button>
            </div>
        );
    }
    return (
        <img
            src={url}
            alt={`게시글 미디어 ${index + 1}`}
            className="media-item"
            onClick={(e) => onClick(index, e)}
            onLoad={(e) =>
                onRatio && onRatio(e.target.naturalWidth, e.target.naturalHeight)
            }
        />
    );
};

// 인스타처럼 원본 비율을 따르되 세로 4:5(0.8) ~ 가로 1.91:1 범위로 제한
const MIN_RATIO = 0.8;
const MAX_RATIO = 1.9133;
const clampRatio = (w, h) =>
    w > 0 && h > 0 ? Math.min(Math.max(w / h, MIN_RATIO), MAX_RATIO) : null;

const MediaCarousel = ({ mediaUrls, onImageClick, className }) => {
    const trackRef = useRef(null);
    const tickingRef = useRef(false);
    const [current, setCurrent] = useState(0);
    const [ratio, setRatio] = useState(null);

    const handleClick = (idx, e) => {
        if (onImageClick) onImageClick(idx, e);
    };

    // 첫 미디어의 비율로 컨테이너 종횡비를 맞춘다(다중도 첫 장 기준 통일)
    const applyRatio = useCallback((w, h) => {
        const r = clampRatio(w, h);
        if (r) setRatio(r);
    }, []);

    const handleScroll = useCallback(() => {
        if (tickingRef.current) return;
        tickingRef.current = true;
        requestAnimationFrame(() => {
            const el = trackRef.current;
            if (el && el.clientWidth > 0) {
                setCurrent(Math.round(el.scrollLeft / el.clientWidth));
            }
            tickingRef.current = false;
        });
    }, []);

    if (!mediaUrls || mediaUrls.length === 0) return null;

    const baseClass = "media-carousel";
    const cls = className ? `${baseClass} ${className}` : baseClass;

    const ratioStyle = ratio ? { aspectRatio: ratio } : undefined;

    if (mediaUrls.length === 1) {
        return (
            <div className={`${cls} single`} style={ratioStyle}>
                <MediaItem
                    url={mediaUrls[0]}
                    index={0}
                    onClick={handleClick}
                    onRatio={applyRatio}
                />
            </div>
        );
    }

    const safeCurrent = Math.min(current, mediaUrls.length - 1);

    return (
        <div className={`${cls} multiple`}>
            <div
                className="media-carousel__track"
                ref={trackRef}
                onScroll={handleScroll}
                data-testid="carousel-track"
                style={ratioStyle}
            >
                {mediaUrls.map((url, idx) => (
                    <div className="media-carousel__slide" key={idx}>
                        <MediaItem
                            url={url}
                            index={idx}
                            onClick={handleClick}
                            onRatio={idx === 0 ? applyRatio : undefined}
                        />
                    </div>
                ))}
            </div>
            <span className="media-carousel__counter">
                {safeCurrent + 1}/{mediaUrls.length}
            </span>
            <div className="media-carousel__dots">
                {mediaUrls.map((_, idx) => (
                    <span
                        key={idx}
                        data-testid="carousel-dot"
                        className={`dot${idx === safeCurrent ? " dot--active" : ""}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default MediaCarousel;
